import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from './ui.js';

/**
 * Assinatura de quem retira, colhida no tablet da portaria.
 *
 * Usa Pointer Events em vez de mouse/touch separados: o mesmo codigo atende
 * dedo, caneta e mouse, e `setPointerCapture` mantem o traco vivo quando o
 * dedo escorrega para fora do quadro no meio da assinatura — sem isso, o
 * traco corta e a pessoa assina de novo.
 *
 * O canvas e desenhado na resolucao real do dispositivo (devicePixelRatio):
 * num tablet retina, um canvas de 300 CSS px sem esse ajuste vira uma imagem
 * de 300 px esticada para 600, e a assinatura sai serrilhada.
 */
export function SignaturePad({
  onChange,
  disabled = false,
}: {
  /** Recebe o PNG em data URL, ou `null` quando o quadro esta em branco. */
  onChange: (dataUrl: string | null) => void;
  disabled?: boolean;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);
  const [hasInk, setHasInk] = useState(false);

  const setup = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    if (rect.width === 0) return;

    canvas.width = Math.round(rect.width * ratio);
    canvas.height = Math.round(rect.height * ratio);

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    // Traco escuro: o fundo do PNG e transparente, e o comprovante pode ser
    // aberto ou impresso sobre branco.
    ctx.strokeStyle = '#111111';
  }, []);

  useEffect(() => {
    setup();
    window.addEventListener('resize', setup);
    return () => window.removeEventListener('resize', setup);
  }, [setup]);

  function positionOf(event: React.PointerEvent<HTMLCanvasElement>) {
    const rect = event.currentTarget.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  }

  function start(event: React.PointerEvent<HTMLCanvasElement>) {
    if (disabled) return;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;

    event.currentTarget.setPointerCapture(event.pointerId);
    drawing.current = true;

    const { x, y } = positionOf(event);
    ctx.beginPath();
    ctx.moveTo(x, y);
    // Um toque simples (assinatura de um ponto so) precisa deixar marca.
    ctx.lineTo(x, y);
    ctx.stroke();
  }

  function move(event: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawing.current || disabled) return;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;

    const { x, y } = positionOf(event);
    ctx.lineTo(x, y);
    ctx.stroke();
  }

  function end() {
    if (!drawing.current) return;
    drawing.current = false;

    const canvas = canvasRef.current;
    if (!canvas) return;

    setHasInk(true);
    onChange(canvas.toDataURL('image/png'));
  }

  function clear() {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    // Limpa em coordenadas do dispositivo: o contexto esta escalado, entao usar
    // as medidas CSS deixaria um canto sujo em tela retina.
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.restore();

    setHasInk(false);
    onChange(null);
  }

  return (
    <div>
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm font-semibold">Assinatura de quem retira</span>
        {hasInk && (
          <Button variant="quiet" onClick={clear} disabled={disabled}>
            Limpar
          </Button>
        )}
      </div>

      <canvas
        ref={canvasRef}
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerCancel={end}
        // `touch-none` impede o navegador de rolar a pagina enquanto o dedo
        // assina — sem isso, assinar arrasta a tela no celular.
        className="mt-2 h-40 w-full touch-none rounded-card border border-line bg-surface-2"
        aria-label="Area de assinatura"
      />

      <p className="mt-2 text-xs text-faint">
        {hasInk
          ? 'Assinatura registrada junto com a confirmacao.'
          : 'Peca para assinar com o dedo na tela.'}
      </p>
    </div>
  );
}
